import { useEffect, useRef, useState } from 'react';
import { api, getSlugFromHostname } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/components';
import styles from './PropertyImageUploader.module.css';

export interface PropertyImage {
  id: string;
  category: 'exterior' | 'floor_plan' | 'room';
  room_type: string | null;
  file_url: string;
  thumb_url: string;
  original_name: string | null;
  mime_type: string;
  file_size: number;
  sort_order: number;
  created_at: string;
}

export interface PropertyImagesResponse {
  exterior: PropertyImage[];
  floor_plan: PropertyImage[];
  room: PropertyImage[];
}

interface Props {
  propertyId: string;
  onChange?: () => void;
}

const CATEGORY_META: Record<'exterior' | 'floor_plan' | 'room', { label: string; emoji: string; limit: number; hint: string }> = {
  exterior: { label: '外観写真', emoji: '🏠', limit: 10, hint: '左から順に表示(1枚目=メイン)' },
  floor_plan: { label: '間取り図', emoji: '📐', limit: 5, hint: 'PDF化時は大きく表示されます' },
  room: { label: '部屋写真', emoji: '🛋', limit: 20, hint: '部屋タイプを選択すると紹介書でキャプションに表示されます' },
};

const ROOM_TYPES = [
  { value: 'living', label: 'リビング' },
  { value: 'kitchen', label: 'キッチン' },
  { value: 'bedroom', label: '寝室' },
  { value: 'bath', label: '浴室' },
  { value: 'toilet', label: 'トイレ' },
  { value: 'entrance', label: '玄関' },
  { value: 'other', label: 'その他' },
];

export function PropertyImageUploader({ propertyId, onChange }: Props) {
  const [images, setImages] = useState<PropertyImagesResponse>({ exterior: [], floor_plan: [], room: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [nextRoomType, setNextRoomType] = useState<string>('living');
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = async () => {
    try {
      const res = await api<PropertyImagesResponse>(`/tenant/properties/${propertyId}/images`);
      setImages(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const uploadFiles = async (category: 'exterior' | 'floor_plan' | 'room', files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    const meta = CATEGORY_META[category];
    const current = images[category].length;
    if (current + list.length > meta.limit) {
      toast(`${meta.label}は最大${meta.limit}枚までです`, 'error');
      return;
    }
    setUploading(u => ({ ...u, [category]: true }));
    try {
      const fd = new FormData();
      fd.append('category', category);
      if (category === 'room') fd.append('room_type', nextRoomType);
      for (const f of list) fd.append('files', f);

      const base = import.meta.env.VITE_API_URL || '/api';
      const { token, user } = useAuthStore.getState();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const slug = getSlugFromHostname() || user?.tenantSlug;
      if (slug) headers['X-Tenant-Slug'] = slug;
      const res = await fetch(`${base}/tenant/properties/${propertyId}/images`, {
        method: 'POST',
        headers,
        body: fd,
      });
      if (!res.ok) {
        let msg = 'アップロードに失敗しました';
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* noop */ }
        toast(msg, 'error');
        return;
      }
      toast(`${list.length}枚アップロードしました`, 'success');
      await load();
      onChange?.();
    } catch (e) {
      console.error(e);
      toast('アップロードに失敗しました', 'error');
    } finally {
      setUploading(u => ({ ...u, [category]: false }));
    }
  };

  const handleDelete = async (category: 'exterior' | 'floor_plan' | 'room', imgId: string) => {
    if (!confirm('この画像を削除しますか？')) return;
    try {
      await api(`/tenant/properties/${propertyId}/images/${imgId}`, { method: 'DELETE' });
      toast('削除しました', 'success');
      await load();
      onChange?.();
    } catch (e) {
      const msg = (e as Error).message || '削除に失敗しました';
      toast(msg, 'error');
    }
  };

  const handleReorder = async (category: 'exterior' | 'floor_plan' | 'room', idx: number, dir: -1 | 1) => {
    const list = [...images[category]];
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    setImages(s => ({ ...s, [category]: list }));
    try {
      await api(`/tenant/properties/${propertyId}/images/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ category, order: list.map(i => i.id) }),
      });
      onChange?.();
    } catch (e) {
      toast('並び替えに失敗しました', 'error');
      await load();
    }
  };

  const onDrop = (category: 'exterior' | 'floor_plan' | 'room', e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(category, e.dataTransfer.files);
    }
  };

  if (loading) return <div className={styles.loading}>画像を読み込み中...</div>;

  const renderCategory = (category: 'exterior' | 'floor_plan' | 'room') => {
    const meta = CATEGORY_META[category];
    const list = images[category];
    const isUploading = !!uploading[category];
    return (
      <div className={styles.categorySection} key={category}>
        <div className={styles.categoryHeader}>
          <div className={styles.categoryTitle}>
            <span>{meta.emoji}</span>
            <span>{meta.label}</span>
            <span className={styles.countBadge}>{list.length}/{meta.limit}</span>
          </div>
          {category === 'room' && (
            <select className={styles.roomTypeSelect} value={nextRoomType} onChange={e => setNextRoomType(e.target.value)}>
              {ROOM_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
            </select>
          )}
          <button
            type="button"
            className={styles.addBtn}
            disabled={isUploading || list.length >= meta.limit}
            onClick={() => inputRefs.current[category]?.click()}
          >
            {isUploading ? 'アップロード中...' : '+ 追加 📷'}
          </button>
          <input
            ref={el => { inputRefs.current[category] = el; }}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              if (e.target.files) uploadFiles(category, e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        <div className={styles.hint}>ℹ {meta.hint}</div>
        <div
          className={styles.grid}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDrop(category, e)}
        >
          {list.map((img, i) => (
            <div key={img.id} className={styles.thumb}>
              <img src={img.thumb_url} alt={img.original_name || ''} />
              {category === 'room' && img.room_type && (
                <div className={styles.roomLabel}>{ROOM_TYPES.find(r => r.value === img.room_type)?.label || img.room_type}</div>
              )}
              <div className={styles.thumbActions}>
                <button type="button" title="上へ" disabled={i === 0} onClick={() => handleReorder(category, i, -1)}>↑</button>
                <button type="button" title="下へ" disabled={i === list.length - 1} onClick={() => handleReorder(category, i, 1)}>↓</button>
                <button type="button" className={styles.delBtn} title="削除" onClick={() => handleDelete(category, img.id)}>×</button>
              </div>
            </div>
          ))}
          {list.length < meta.limit && (
            <div className={styles.dropzone} onClick={() => inputRefs.current[category]?.click()}>
              <span>+</span>
              <div>クリック or<br />D&amp;D</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      {renderCategory('exterior')}
      {renderCategory('floor_plan')}
      {renderCategory('room')}
      <div className={styles.footerNote}>
        ℹ JPG / PNG / WebP、1枚あたり15MBまで。Sharp でリサイズ・WebP 変換されます
      </div>
    </div>
  );
}
